import type { MutationMeta, QueryClient } from '@tanstack/react-query'

/**
 * Query roots of summaries computed ACROSS modules (FF-AUDIT-011).
 *
 * Entity-360 hubs, problem lineage, report runs and dashboards read from many
 * modules at once; a feature's mutation invalidates its own family
 * (`['parts']`, `['governance']`…) and cannot know that a stock transfer
 * changes the part's hub card or that a hold release changes the legal-hold
 * report. So every successful write — through the react-query mutation cache
 * OR the offline runtime's direct path — invalidates these roots. Only
 * mounted queries refetch; the rest are marked stale.
 */
export const DERIVED_VIEW_ROOTS: readonly (readonly string[])[] = [
  ['search', 'hub'],
  ['search', 'lineage'],
  ['reports', 'run'],
  ['dashboard'],
  ['report-dashboards'],
]

/**
 * FF-QA-046: a mutation that provably changes none of the derived views opts
 * out with `meta: { derivedViews: false }` — a checklist tick or a root-cause
 * save on a CAR alters neither its lineage nor a signature, yet every such
 * write refetched the lineage graph four times and the e-sign trail three.
 * A mutation may instead name the roots it does touch. Anything else (no
 * meta, or any other value) keeps the FF-AUDIT-011 default: sweep everything.
 */
export interface DerivedViewsMeta {
  derivedViews?: false | readonly (readonly string[])[] | undefined
}

/** The roots a mutation's `meta` asks to invalidate: all, some, or none. */
export function derivedViewRootsFor(meta: MutationMeta | DerivedViewsMeta | undefined): readonly (readonly string[])[] {
  const choice = (meta as DerivedViewsMeta | undefined)?.derivedViews
  if (choice === false) return []
  if (Array.isArray(choice)) return choice as readonly (readonly string[])[]
  return DERIVED_VIEW_ROOTS
}

export function invalidateDerivedViews(queryClient: QueryClient, meta?: MutationMeta | DerivedViewsMeta | undefined): void {
  for (const queryKey of derivedViewRootsFor(meta)) void queryClient.invalidateQueries({ queryKey })
}
